import { useRef } from 'react';
import { ListSearchControls } from '@/components/ListSearchControls/ListSearchControls';
import { ProvideSearchAPIContext, useSearchApiContext } from '@/components/ListSearchControls/contexts';
import type { SearchOptions } from '@/components/ListSearchControls/types';
import { useApi } from '@/lib/legacy';
import { policySearchOptions } from '../../constants/searchData/searchOptions';
import * as hooks from '../../hooks';
import { PoliciesTable, type Policy, type PoliciesTableHandle } from './PoliciesTable';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: hooks/useAddProductOptionsToSearchOptions.js
const useAddProductOptionsToSearchOptions = (options: unknown): SearchOptions => (hooks as any).useAddProductOptionsToSearchOptions(options);

/**
 * The Policies list: the shared search toolbar over one table of policies. Search, filters and
 * sort live in the URL and the PoliciesListView search settings, as before; the table loads the
 * next page as the user scrolls (D15–D17).
 */
export const PoliciesListView = ({ data = undefined, searchPath }: { data?: Policy[]; searchPath?: string }) => {
  const listRef = useRef<PoliciesTableHandle>(null);
  const { getPolicies } = useApi();
  const searchOptions = useAddProductOptionsToSearchOptions(policySearchOptions);

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white px-4 pt-16 pb-6 md:px-[120px]">
      <ProvideSearchAPIContext
        {...(data !== undefined ? { data } : {})}
        {...(searchPath !== undefined ? { searchPath } : {})}
        searchOptions={searchOptions}
        getData={getPolicies as (...args: unknown[]) => Promise<unknown>}
        appSettingsKey="PoliciesListView"
        cacheKey="policylist"
        keepRowsOnFailedPage
        listRef={listRef}
      >
        <div className="pb-4">
          <ListSearchControls heading="Policies" searchPlaceholder="Search for policies" showDisplayTypeToggle={false} />
        </div>
        <Policies tableRef={listRef} />
      </ProvideSearchAPIContext>
    </div>
  );
};

const Policies = ({ tableRef }: { tableRef: React.RefObject<PoliciesTableHandle> }) => {
  const { data, loading, loadingFromCache, requestingMore, hasMore, failed, requestMoreData } = useSearchApiContext();
  return (
    <PoliciesTable
      ref={tableRef}
      rows={data as Policy[]}
      loading={loading || requestingMore}
      fetching={loading || !!loadingFromCache || requestingMore}
      failed={failed}
      hasMore={hasMore}
      onLoadMore={requestMoreData}
    />
  );
};

export default PoliciesListView;
